import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

// â”€â”€ Dynamic image loading via require.context â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Automatically picks up every image in each folder.
// Add / remove files in the folder â†’ code never needs to change.

// Banner images â€“ Home page hero carousel (1.jpg, 2.jpg â€¦ 11.jpg)
const _bannerCtx = require.context(
  "../assets/Banner",
  false,
  /\.(png|jpe?g|webp|gif)$/i
);
const _bannerImgs = _bannerCtx
  .keys()
  .sort((a, b) => {
    const n = (s) => parseInt(s.replace(/\D/g, "") || "0", 10);
    return n(a) - n(b);
  })
  .map(_bannerCtx);

// Gallery images â€“ sorted numerically (1.jpeg, 2.jpeg â€¦ 18.jpeg)
const _galleryCtx = require.context(
  "../assets/gallery",
  false,
  /\.(png|jpe?g|webp|gif)$/i
);
const fullGalleryImages = _galleryCtx
  .keys()
  .sort((a, b) => {
    const n = (s) => parseInt(s.replace(/\D/g, "") || "0", 10);
    return n(a) - n(b);
  })
  .map(_galleryCtx);

// Hero banner: use Banner folder images (1â€“11) in order; fallback to gallery if empty
const images = _bannerImgs.length > 0 ? _bannerImgs : fullGalleryImages.slice(0, 3);

// Facilities images
const _facCtx = require.context(
  "../assets/Facilities",
  false,
  /\.(png|jpe?g|webp|gif)$/i
);
const facilitiesImages = _facCtx.keys().map(_facCtx);

// Partner logos â€“ all files in Our-Client folder, no manual list needed
const _partnerCtx = require.context(
  "../assets/Our-Client",
  false,
  /\.(png|jpe?g|webp|gif)$/i
);
const partnerLogos = _partnerCtx.keys().map(_partnerCtx);

// â”€â”€ Transparent 1Ã—1 px fallback for broken images â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E";
const onImgError = (e) => { e.currentTarget.src = PLACEHOLDER; };

function Home() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const timeoutRef = useRef(null);

  // Animated headline variants
  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.08 },
    },
  };

  const wordVariants = {
    hidden: { opacity: 0, y: 18, filter: "blur(6px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { type: "spring", stiffness: 320, damping: 26 },
    },
  };

  const renderAnimatedLine = (text, extraClass = "") => (
    <motion.div
      className={`flex flex-wrap justify-center gap-x-2 ${extraClass}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {text.split(" ").map((word, idx) => (
        <motion.span
          key={`${word}-${idx}`}
          variants={wordVariants}
          className="inline-block bg-gradient-to-r from-white via-white/90 to-white/80 bg-clip-text text-transparent"
        >
          {word}
        </motion.span>
      ))}
    </motion.div>
  );

  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    // Rotate images every 7 seconds for balanced focus per image
    timeoutRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000);

    return () => {
      clearInterval(timeoutRef.current);
    };
  }, []);

  return (
    <div>
      {/* Hero Section */}

<div className="relative h-[95vh] w-full overflow-hidden">
  {/* Subtle gradient overlays for premium look */}
  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50 z-10" />
  <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/50 to-transparent z-10" />

  {/* Image Carousel with crossfade + Ken Burns effect */}
  <AnimatePresence mode="sync">
    <motion.img
      key={currentIndex}
      src={images[currentIndex]}
      alt="Hero Banner"
      className="absolute inset-0 w-full h-full object-cover"
      initial={{ opacity: 0, scale: 1.02 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0.0, scale: 1.01 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
      style={{ willChange: "opacity, transform" }}
      onError={onImgError}
      draggable={false}
    />
  </AnimatePresence>

  {/* Animated Title Overlay */}
  <div
  className="absolute inset-0 flex flex-col justify-center items-center text-center px-4 z-20"
  style={{ perspective: "1200px" }}
>
  <AnimatePresence mode="wait">
    <motion.div
      key={`headline-${currentIndex}`}
      initial={{ opacity: 0, rotateX: -90, scale: 0.9 }}
      animate={{ opacity: 1, rotateX: 0, scale: 1 }}
      exit={{ opacity: 0, rotateX: 90, scale: 0.9 }}
      transition={{
        duration: 1, // slow & elegant
        ease: [0.25, 0.1, 0.25, 1], // smooth cubic easing
      }}
      className="text-white drop-shadow-[0_8px_22px_rgba(0,0,0,0.65)]"
      style={{
        willChange: "opacity, transform",
        transformStyle: "preserve-3d",
      }}
    >
      {renderAnimatedLine(
        "An Exotic Resort",
        "text-2xl md:text-4xl xl:text-5xl font-fancy tracking-wide"
      )}
      {renderAnimatedLine(
        "The Best Resort In Purbachal",
        "text-4xl md:text-5xl xl:text-6xl font-extrabold mt-3"
      )}
    </motion.div>
  </AnimatePresence>
</div>


      {/* Spacer */}
      </div>
      
      {/* Welcome Section */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 text-center pt-8 md:pt-10">
        <h2 className="text-3xl md:text-4xl font-bold text-blue-800 mb-4 tracking-wide font-fancy">
          WELCOME TO
        </h2>
        <h3 className="text-2xl md:text-3xl font-semibold text-black mb-6 font-fancy">
        Chuti Resort Purbachal
        </h3>
        <p className="text-black text-justify">
          Chuti Resort Purbachal is located in Purbachal, near Dhaka. Chuti Resort Purbachal
          offers a heavenly romantic ambiance for those seeking a healthy lifestyle retreat.
          The resort combines luxury accommodations and lifestyle retreats with a wide range
          of facilities like a swimming pool, green field, walkway, organized plantation &amp;
          ponds, and flower gardening. Whether you are a fitness enthusiast, nature lover,
          or simply in search of tranquility, Chuti Resort Purbachal provides an immersive
          experience that revitalizes both the body and the spirit.
        </p>

        {/* Button */}
        <div className="mt-6 md:mt-8">
          <Link
            to="/about"
            className="inline-block bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2 px-6 rounded-md shadow-lg transition duration-300 transform hover:scale-105 font-fancy"
          >
            Find More
          </Link>
        </div>
      </div>


      {/* Fancy Gallery */}
      <div className="py-6 md:py-8">
        <FancyGallery />
      </div>

      {/* Partners Section */}
      <PartnersSection />

      {/* Full Gallery Section */}
      <GallerySection />

      {/* Google Map Section */}
      <div className="w-full px-4 md:px-8 pb-6 md:pb-8">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14593.019407508606!2d90.505132!3d23.88058!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755d35b4d2d86eb%3A0x2e2a0bdde7fe10c0!2sChuti%20Resort%20Purbachal!5e0!3m2!1sen!2sbd!4v1773386915534!5m2!1sen!2sbd"
          width="100%"
          height="300"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="rounded-lg shadow-lg w-full h-[250px] md:h-[350px] lg:h-[400px]"
          title="Google Maps - Chuti Resort Purbachal Location"
        ></iframe>
      </div>
    </div>
  );
}

// FACILITIES & SERVICES Component
const FancyGallery = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <div className="w-full px-4 md:px-8">
      {/* Section Header */}
      <motion.div
        className="text-center mb-6 md:mb-7"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-widest font-fancy bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-800 bg-clip-text text-transparent uppercase">
          Facilities &amp; Services
        </h2>
        <div className="flex items-center justify-center gap-3 mt-3">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500" />
          <div className="w-2 h-2 rounded-full bg-amber-500" />
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500" />
        </div>
        <p className="mt-3 text-gray-600 text-sm md:text-base max-w-xl mx-auto">
          Experience world-class amenities crafted for your comfort and luxury
        </p>
      </motion.div>

      {/* Accordion-style expandable panels */}
      <div className="relative flex items-stretch justify-center overflow-hidden h-[260px] md:h-[380px] lg:h-[460px] rounded-2xl shadow-2xl">
        {facilitiesImages.map((imgSrc, index) => {
          const isHovered = hoveredIndex === index;
          const isOther = hoveredIndex !== null && !isHovered;

          return (
            <motion.div
              key={index}
              className="relative overflow-hidden cursor-pointer"
              animate={{
                flex: isHovered ? 4 : isOther ? 0.6 : 1,
              }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <img
                src={imgSrc}
                alt={`Facility ${index + 1}`}
                className="w-full h-full object-cover select-none"
                draggable={false}
                onError={onImgError}
              />
              {/* Overlay gradient */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent transition-opacity duration-500"
                style={{ opacity: isHovered ? 1 : 0.35 }}
              />
              {/* Label on hover */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 p-4"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 16 }}
                transition={{ duration: 0.35 }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-8 rounded-full bg-amber-400" />
                  <span className="text-white font-semibold text-base md:text-lg font-fancy drop-shadow-md">
                    Our Facilities
                  </span>
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const PartnersSection = () => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check on mount
    checkMobile();
    
    // Use resize event instead of unload
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Triple the logos to ensure smooth infinite scroll showing all logos
  const scrollingLogos = [...partnerLogos, ...partnerLogos, ...partnerLogos];

  return (
    <section className="px-4 md:px-6 py-4 md:py-5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="text-center mb-6"
      >
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-blue-900 font-fancy mb-3"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          OUR PARTNERS
        </motion.h2>

        <motion.div
          className="max-w-4xl mx-auto mt-6"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3 font-fancy">
            OUR COMPANIES INCORPORATED WITH
          </h3>
          <p className="text-base text-black leading-relaxed">
            The aroma of nature is so diverse, that it brings the wish to feel serenity from
            different types of architecture. Chuti Resort Purbachal provides those environments to you
            in well designed villas and rooms.
          </p>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative overflow-hidden rounded-[32px] border border-white/40 bg-white/5 backdrop-blur-[2px] p-3 md:p-6"
        style={{
          maskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
        }}
      >
        <motion.div
          className="flex gap-4 sm:gap-5 md:gap-8"
          animate={{ x: ["0%", "-33.333%"] }}
          transition={{
            duration: isMobile ? 8 : 5,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          {scrollingLogos.map((logo, index) => (
            <div
              key={`partner-${index}`}
              className="flex-shrink-0 w-36 h-20 sm:w-44 sm:h-24 md:w-52 md:h-28 flex items-center justify-center bg-white rounded-2xl shadow-md border border-gray-100 px-4 sm:px-5 md:px-6"
            >
              <img
                src={logo}
                alt="Partner logo"
                className="max-w-full max-h-full object-contain"
                loading="lazy"
                onError={onImgError}
              />
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};


// â”€â”€ FULL GALLERY SECTION â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const GallerySection = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImg = () =>
    setLightboxIndex((i) => (i - 1 + fullGalleryImages.length) % fullGalleryImages.length);
  const nextImg = () =>
    setLightboxIndex((i) => (i + 1) % fullGalleryImages.length);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") setLightboxIndex((i) => i !== null ? (i - 1 + fullGalleryImages.length) % fullGalleryImages.length : null);
      if (e.key === "ArrowRight") setLightboxIndex((i) => i !== null ? (i + 1) % fullGalleryImages.length : null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Masonry-like layout: assign span classes by index pattern
  const getSpanClass = (idx) => {
    const pattern = idx % 6;
    if (pattern === 0) return "col-span-2 row-span-2";
    if (pattern === 3) return "col-span-2 row-span-1";
    return "col-span-1 row-span-1";
  };

  return (
    <section className="px-4 md:px-8 py-6 md:py-8">
      {/* Header */}
      <motion.div
        className="text-center mb-6 md:mb-8"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.75, ease: "easeOut" }}
      >
        <h2 className="text-3xl md:text-4xl font-extrabold font-fancy tracking-widest uppercase bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-800 bg-clip-text text-transparent">
          Photo Gallery
        </h2>
        <div className="flex items-center justify-center gap-3 mt-3">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500" />
          <div className="w-2 h-2 rounded-full bg-amber-500" />
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500" />
        </div>
        <p className="mt-3 text-gray-600 text-sm md:text-base max-w-xl mx-auto">
          Glimpses of beauty, nature and luxury at Chuti Resort Purbachal
        </p>
      </motion.div>

      {/* Masonry Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[160px] md:auto-rows-[200px] gap-2 md:gap-3 max-w-7xl mx-auto">
        {fullGalleryImages.map((src, idx) => (
          <motion.div
            key={idx}
            className={`${getSpanClass(idx)} relative overflow-hidden rounded-xl cursor-pointer group`}
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: (idx % 6) * 0.06, ease: "easeOut" }}
            onClick={() => openLightbox(idx)}
          >
            <img
              src={src}
              alt={`Gallery ${idx + 1}`}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              draggable={false}
              loading="lazy"
              onError={onImgError}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-400 flex items-center justify-center">
              <motion.div
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-3"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </motion.div>
            </div>
            {/* Index badge */}
            <div className="absolute top-2 right-2 bg-black/50 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {idx + 1}/{fullGalleryImages.length}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            className="fixed inset-0 z-[100] flex flex-col bg-black/95"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            {/* Top bar */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 md:px-6 py-3 bg-black/40 backdrop-blur-sm border-b border-white/10">
              <span className="text-white font-semibold text-base">Photo Gallery</span>
              <span className="text-white/60 text-sm mx-4">{lightboxIndex + 1} / {fullGalleryImages.length}</span>
              <button onClick={closeLightbox} className="w-9 h-9 rounded-full bg-white/10 hover:bg-red-500 text-white flex items-center justify-center transition-colors duration-200" aria-label="Close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {/* Image area */}
            <div className="flex-1 min-h-0 relative flex items-center justify-center px-14 md:px-20 py-3">
              <button onClick={prevImg} className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-amber-500 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm z-10" aria-label="Previous">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <AnimatePresence mode="wait">
                <motion.img
                  key={lightboxIndex}
                  src={fullGalleryImages[lightboxIndex]}
                  alt={`Gallery ${lightboxIndex + 1}`}
                  className="max-h-full max-w-full object-contain rounded-xl shadow-2xl"
                  style={{ maxHeight: "calc(100vh - 130px)" }}
                  draggable={false}
                  onError={onImgError}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.2 }}
                />
              </AnimatePresence>
              <button onClick={nextImg} className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-amber-500 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm z-10" aria-label="Next">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
            {/* Thumbnail strip */}
            <div className="flex-shrink-0 flex gap-2 overflow-x-auto px-4 py-3 bg-black/40 border-t border-white/10" style={{ scrollbarWidth: "none" }}>
              {fullGalleryImages.map((src, idx) => (
                <button key={idx} onClick={() => setLightboxIndex(idx)}
                  className={`flex-shrink-0 w-14 h-10 md:w-16 md:h-11 rounded-lg overflow-hidden border-2 transition-all duration-200 ${idx === lightboxIndex ? "border-amber-400 opacity-100 scale-105" : "border-transparent opacity-50 hover:opacity-80 hover:border-white/40"}`}>
                  <img src={src} alt={`thumb ${idx + 1}`} className="w-full h-full object-cover" onError={onImgError} />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Home;
